import projections from "../../api/projections";

/**
 * Module Vuex pour la gestion des projections de contrats.
 * Gère l'état des clients éligibles, des formulaires de projection
 * et des résultats de calcul.
 */
export default {
  namespaced: true,
  state: {
    eligibleCustomers: [],
    selectedCustomer: null,
    projectionData: null,
    projectionResults: null,
    projectionScenarios: [], // Nouveau: stocke plusieurs scénarios de projection
    currentYearStats: null, // Stats N-1 pour affichage dans le formulaire
    loading: false,
    loadingExport: false,
    error: null,
    currentYear: new Date().getFullYear(),
    projectionForm: {
      customerId: null,
      projectionYear: new Date().getFullYear(),
      capitalProjection: 0,
      implantProjection: 0,
      serviceProjection: 0,
      consumableProjection: 0,
    },
    defaultForm: {
      customerId: null,
      projectionYear: new Date().getFullYear(),
      capitalProjection: 0,
      implantProjection: 0,
      serviceProjection: 0,
      consumableProjection: 0,
    },
  },
  mutations: {
    setEligibleCustomers(state, customers) {
      state.eligibleCustomers = customers;
    },
    setSelectedCustomer(state, customer) {
      state.selectedCustomer = customer;
      if (customer) {
        state.projectionForm.customerId = customer.id;
      }
    },
    setCurrentYearStats(state, stats) {
      state.currentYearStats = stats;
    },
    setProjectionResults(state, results) {
      state.projectionResults = results;
    },
    addProjectionScenario(state, scenario) {
      // Ajoute un scénario avec un nom unique
      const scenarioWithName = {
        ...scenario,
        name:
          scenario.name || `Scénario ${state.projectionScenarios.length + 1}`,
        id: Date.now(),
      };
      state.projectionScenarios.push(scenarioWithName);
    },
    removeProjectionScenario(state, scenarioId) {
      state.projectionScenarios = state.projectionScenarios.filter(
        (s) => s.id !== scenarioId,
      );
    },
    clearProjectionScenarios(state) {
      state.projectionScenarios = [];
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setLoadingExport(state, loading) {
      state.loadingExport = loading;
    },
    setError(state, error) {
      state.error = error;
    },
    updateProjectionForm(state, formData) {
      state.projectionForm = { ...state.projectionForm, ...formData };
    },
    resetProjectionForm(state) {
      state.projectionForm = {
        ...state.defaultForm,
        projectionYear: new Date().getFullYear(),
        capitalProjection: 0,
        implantProjection: 0,
        serviceProjection: 0,
        consumableProjection: 0,
      };
      state.selectedCustomer = null;
      state.projectionResults = null;
      state.projectionScenarios = [];
      state.error = null;
      state.projectionData = null;
      state.currentYearStats = null;
    },
    setProjectionYear(state, year) {
      state.projectionForm.projectionYear = year;
    },
  },
  actions: {
    /**
     * Récupère la liste des clients éligibles pour une projection
     */
    async fetchEligibleCustomers({ commit }) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const response = await projections.getEligibleCustomers();
        commit("setEligibleCustomers", response.data);
        return response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          "Erreur lors de la récupération des clients éligibles";
        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Charge les statistiques de l'année en cours pour un client
     */
    async fetchCurrentYearStats({ commit, state }) {
      if (!state.selectedCustomer?.id) {
        return;
      }

      commit("setLoading", true);
      commit("setError", null);
      try {
        const payload = {
          customerId: state.selectedCustomer.id,
          projectionYear: state.currentYear,
          capitalInput: { inputType: "EurosAmount", value: 0 },
          implantInput: { inputType: "EurosAmount", value: 0 },
          serviceInput: { inputType: "EurosAmount", value: 0 },
          consommableInput: { inputType: "EurosAmount", value: 0 },
        };
        const response = await projections.calculate(payload);
        commit("setCurrentYearStats", response.data);
        return response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          "Erreur lors de la récupération des statistiques";
        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Calcule une projection de contrat
     */
    async calculateProjection({ commit, state }) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        // Transformation des données au format attendu par l'API
        const payload = {
          customerId: state.projectionForm.customerId,
          projectionYear: state.projectionForm.projectionYear,
          capitalInput: {
            inputType: "EurosAmount",
            value: state.projectionForm.capitalProjection,
          },
          implantInput: {
            inputType: "EurosAmount",
            value: state.projectionForm.implantProjection,
          },
          serviceInput: {
            inputType: "EurosAmount",
            value: state.projectionForm.serviceProjection,
          },
          consommableInput: {
            inputType: "EurosAmount",
            value: state.projectionForm.consumableProjection,
          },
        };

        // Ajouter overrideStatusId si défini
        if (
          state.projectionForm.overrideStatusId !== null &&
          state.projectionForm.overrideStatusId !== undefined
        ) {
          payload.overrideStatusId = state.projectionForm.overrideStatusId;
        }

        const response = await projections.calculate(payload);
        commit("setProjectionResults", response.data);
        return response.data;
      } catch (error) {
        let errorMessage = "Erreur lors du calcul de la projection";

        if (error.response) {
          switch (error.response.status) {
            case 400:
              if (error.response.data.message.includes("active contract")) {
                errorMessage =
                  "Ce client possède déjà un contrat actif. Vous ne pouvez pas créer de projection.";
              } else if (error.response.data.message.includes("year")) {
                errorMessage =
                  "L'année de projection doit être l'année en cours ou la suivante.";
              } else {
                errorMessage = error.response.data.message;
              }
              break;
            case 403:
              errorMessage =
                "Vous n'avez pas les droits pour accéder à cette fonctionnalité.";
              break;
            case 404:
              errorMessage = "Client introuvable.";
              break;
            default:
              errorMessage = error.response.data.message || errorMessage;
          }
        }

        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Sélectionne un client pour créer une projection
     */
    selectCustomer({ commit }, customer) {
      commit("setSelectedCustomer", customer);
      commit("setCurrentYearStats", null); // Reset des stats précédentes
      // Réinitialiser les champs de projection
      commit("updateProjectionForm", {
        capitalProjection: 0,
        implantProjection: 0,
        serviceProjection: 0,
        consumableProjection: 0,
      });
    },

    /**
     * Met à jour les données du formulaire de projection
     */
    updateForm({ commit }, formData) {
      commit("updateProjectionForm", formData);
    },

    /**
     * Réinitialise les champs de projection uniquement
     */
    resetProjectionFields({ commit }) {
      commit("updateProjectionForm", {
        capitalProjection: 0,
        implantProjection: 0,
        serviceProjection: 0,
        consumableProjection: 0,
      });
    },

    /**
     * Réinitialise le formulaire et les résultats
     */
    resetProjection({ commit }) {
      commit("resetProjectionForm");
    },

    /**
     * Exporte les projections en PDF
     */
    async exportProjectionsToPdf({ commit }, exportData) {
      commit("setLoadingExport", true);
      commit("setError", null);
      try {
        const response = await projections.exportToPdf(exportData);
        return response;
      } catch (error) {
        let errorMessage = "Erreur lors de l'export PDF";

        if (error.response) {
          switch (error.response.status) {
            case 400:
              errorMessage =
                "Vous devez avoir entre 1 et 5 scénarios pour exporter.";
              break;
            case 500:
              errorMessage =
                "Erreur serveur lors de la génération du PDF. Veuillez réessayer.";
              break;
            default:
              errorMessage =
                error.response.data?.message || "Erreur lors de l'export PDF";
          }
        }

        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoadingExport", false);
      }
    },

    /**
     * Calcule les points pour un montant donné
     */
    async calculatePoints({ commit }, calculationData) {
      try {
        const response = await projections.calculatePoints(calculationData);
        return response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Erreur lors du calcul des points";
        commit("setError", errorMessage);
        throw error;
      }
    },
  },
  getters: {
    /**
     * Retourne les années valides pour la projection (année en cours et suivante)
     */
    validProjectionYears: (state) => {
      return [state.currentYear, state.currentYear + 1];
    },

    /**
     * Vérifie si le formulaire est valide
     */
    isFormValid: (state) => {
      return (
        state.projectionForm.customerId && state.projectionForm.projectionYear
      );
    },

    /**
     * Calcule le total du CA projeté
     */
    totalProjectedSales: (state) => {
      return (
        state.projectionForm.capitalProjection +
        state.projectionForm.implantProjection +
        state.projectionForm.serviceProjection +
        state.projectionForm.consumableProjection
      );
    },

    /**
     * Retourne les clients avec historique de ventes
     */
    customersWithHistory: (state) => {
      return state.eligibleCustomers.filter((c) => c.hasSalesHistory);
    },

    /**
     * Retourne les clients sans historique de ventes
     */
    customersWithoutHistory: (state) => {
      return state.eligibleCustomers.filter((c) => !c.hasSalesHistory);
    },
  },
};
